import type { ReactElement } from 'react';

import { useDismissVacancyComplaintMutation } from '@/api/vacancyComplaintsApi';
import { Modal } from '@/components/Modal';

interface DismissComplaintModalProps {
    isOpen: boolean;
    complaintId: string;
    onClose: () => void;
    onResolved: () => void;
    onError: (message: string) => void;
}

/**
 * Confirm-модал действия «Без нарушений» (design-spec §2.5): закрывает жалобу
 * без действий над вакансией → `POST /vacancy-complaints/:id/dismiss`.
 */
export function DismissComplaintModal({
    isOpen,
    complaintId,
    onClose,
    onResolved,
    onError
}: DismissComplaintModalProps): ReactElement {
    const [dismissComplaint, { isLoading: isDismissing }] =
        useDismissVacancyComplaintMutation();

    const submit = async (): Promise<void> => {
        try {
            await dismissComplaint({ id: complaintId }).unwrap();
            onResolved();
        } catch {
            onError('Не удалось закрыть жалобу');
            onClose();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Без нарушений"
            testId="vacancy-complaint-dismiss-modal"
        >
            <div className="space-y-4" data-testid="vacancy-complaint-dismiss-form">
                <p className="text-sm text-gray-600">
                    Жалоба будет закрыта без действий. Вакансия останется без изменений.
                </p>
                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-100"
                    >
                        Отмена
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            void submit();
                        }}
                        disabled={isDismissing}
                        data-testid="vacancy-complaint-dismiss-submit"
                        className="rounded bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800 disabled:opacity-50"
                    >
                        Закрыть жалобу
                    </button>
                </div>
            </div>
        </Modal>
    );
}
